import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { DialogContent } from '@material-ui/core';

import { sessionService, AccountForms, FormStatus, accountService } from '@lib/state';
import {
  SignInForm,
  SignInFormFields,
  useFormEvents,
  ForgotPasswordForm,
  ForgotPasswordFormFields,
  StyledDialog,
} from '@lib/common';
import { CreateAccountWidget } from './';

interface Props {
  show: boolean;
  redirectPath: string;
  onClose: () => void;
}

export const SignInModal: React.FC<Props> = ({ show, redirectPath, onClose }) => {
  const [form, setForm] = useState<'SignIn' | 'ForgotPassword'>('SignIn');
  const history = useHistory();
  const [loginResult, resetLoginFormUI] = useFormEvents(AccountForms.Login);
  const [forgotPasswordResult, resetForgotPasswordFormUI] = useFormEvents(
    AccountForms.ForgotPassword
  );

  const onLoginSubmit = ({ username, password }: SignInFormFields) => {
    sessionService.login(username, password);
  };

  const onForgotPasswordRedirect = () => {
    setForm('ForgotPassword');
  };

  const onForgotPasswordSubmit = ({ username }: ForgotPasswordFormFields) => {
    accountService.forgotPassword(username);
  };

  const onForgotPasswordCancel = () => {
    setForm('SignIn');
  };

  useEffect(() => {
    if (loginResult.status === FormStatus.Success) {
      onClose();
      history.push(redirectPath);
    }
    // eslint-disable-next-line
  }, [loginResult.status]);

  const renderForm = (): React.ReactNode => {
    switch (form) {
      case 'ForgotPassword': {
        return (
          <ForgotPasswordForm
            pending={forgotPasswordResult.status === FormStatus.Pending}
            success={forgotPasswordResult.status === FormStatus.Success}
            error={forgotPasswordResult.error}
            onSubmit={onForgotPasswordSubmit}
            onResetFeedback={resetForgotPasswordFormUI}
            onCancel={onForgotPasswordCancel}
          />
        );
      }
      default: {
        return (
          <>
            <SignInForm
              buttonColor="secondary"
              onSubmit={onLoginSubmit}
              error={loginResult.error}
              pending={loginResult.status === FormStatus.Pending}
              onResetFeedback={resetLoginFormUI}
              usernameLabel="Username"
              onForgotPasswordRedirect={onForgotPasswordRedirect}
            >
              <CreateAccountWidget
                onCreate={() => {
                  onClose();
                  history.push('/account/create');
                }}
              />
            </SignInForm>
          </>
        );
      }
    }
  };

  const title = () => {
    switch (form) {
      case 'ForgotPassword':
        return 'Forgot Password';
      default:
        return 'Login';
    }
  };

  return (
    <StyledDialog title={title()} onClose={onClose} open={show}>
      <DialogContent>{renderForm()}</DialogContent>
    </StyledDialog>
  );
};
